"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useTelehealthTranscriptStream } from "@/hooks/use-telehealth-transcript-stream";
import { CreateNoteState } from "@/types/enums/note-state";
import type { NoteDetail } from "@/types/notes";
import { useScribeStore } from "@/stores/scribe-store";

/**
 * Drive the unified recording state machine for telehealth notes.
 *
 * Mic notes mutate the recording slice from `MediaRecorder` callbacks; this
 * hook plays the same adapter role for telehealth — webhooks and the WS
 * bridge are the "MediaRecorder," and the bot embedded in the `NoteDetail`
 * cache (live-patched by `useTelehealthBotLiveUpdates`) exposes their effects
 * via TanStack Query. The PIP reads the recording slice and renders; it does
 * not know Recall exists.
 *
 * Transition table:
 *   - Bot in the call (`recallBotStatus === "listening"`) → register +
 *     `recordingNoteState = RECORDING`. We don't override an optimistic
 *     PAUSED — Recall has no distinct "paused" status (both `recording`
 *     and `not_recording` collapse to "listening"), so the store's local
 *     pause flag is the source of truth for that transition.
 *   - Bot left the meeting (`recallBotStatus === "ended"`) but the
 *     authoritative transcript hasn't been reconciled yet → `recordingNoteState
 *     = PAUSED`, `isTranscribing = false`. This is NOT a loading state — the
 *     Generate button is simply disabled (gated on the bot status in ai-note)
 *     and the PIP shows "finalizing transcript…" until `transcript.done` lands.
 *   - Transcript terminal (`recallBotStatus ∈ {transcribed,
 *     transcribe_failed, failed}`) → identical slice state (PAUSED, not
 *     transcribing); the only difference is ai-note now enables Generate. The
 *     note is only ever generated from the authoritative transcript (or the
 *     best-available live text on `transcribe_failed`). The recording slice
 *     stays set so the PIP keeps surfacing the note (the user still needs to
 *     Generate from it); it renders inert chrome (grey dot, play disabled).
 *
 * Mounted once globally inside `RecordingPiP` so the transitions still fire
 * if the provider has navigated away from the playground.
 */
export function useTelehealthRecordingSync(note) {
  const $ = _c(17);
  const noteId = note?.id;
  const bot = note?.telehealthBot ?? null;
  const isTelehealthNote = !!bot?.recallBotId && bot?.noteId === noteId;
  const botStatus = bot?.recallBotStatus ?? null;
  const tailDrained = isTelehealthNote && !!bot?.recallBotEndedAt;
  const transcriptReady = isTelehealthNote && (botStatus === "transcribed" || botStatus === "transcribe_failed" || botStatus === "failed");
  const isInProgress = isTelehealthNote && !tailDrained;
  useTelehealthTranscriptStream(noteId ?? null, isInProgress);
  const setRecordingId = useScribeStore(_temp);
  const setAudioSource = useScribeStore(_temp2);
  const setRecordingNoteState = useScribeStore(_temp3);
  const setIsTranscribing = useScribeStore(_temp4);
  let t0;
  let t1;
  if ($[0] !== botStatus || $[1] !== isTelehealthNote || $[2] !== noteId || $[3] !== setAudioSource || $[4] !== setIsTranscribing || $[5] !== setRecordingId || $[6] !== setRecordingNoteState || $[7] !== tailDrained || $[8] !== transcriptReady) {
    t0 = () => {
      if (!isTelehealthNote || !noteId) {
        if (noteId) {
          const s_3 = useScribeStore.getState();
          if (s_3.currentRecordingId === noteId && s_3.audioSource === "telehealth") {
            s_3.setAudioSource(undefined);
          }
        }
        return;
      }
      const state = useScribeStore.getState();
      const stillTrackingThisNote = state.currentRecordingId === noteId;
      if (transcriptReady || botStatus === "ended" || tailDrained) {
        if (!stillTrackingThisNote) {
          return;
        }
        if (state.audioSource !== "telehealth") {
          setAudioSource("telehealth");
        }
        if (state.isTranscribing) {
          setIsTranscribing(false);
        }
        if (state.recordingNoteState !== CreateNoteState.PAUSED) {
          setRecordingNoteState(CreateNoteState.PAUSED);
        }
        return;
      }
      if (!stillTrackingThisNote) {
        setRecordingId(noteId);
      }
      if (state.audioSource !== "telehealth") {
        setAudioSource("telehealth");
      }
      if (state.recordingNoteState !== CreateNoteState.RECORDING && state.recordingNoteState !== CreateNoteState.PAUSED) {
        setRecordingNoteState(CreateNoteState.RECORDING);
      }
    };
    t1 = [isTelehealthNote, noteId, tailDrained, transcriptReady, botStatus, setRecordingId, setAudioSource, setRecordingNoteState, setIsTranscribing];
    $[0] = botStatus;
    $[1] = isTelehealthNote;
    $[2] = noteId;
    $[3] = setAudioSource;
    $[4] = setIsTranscribing;
    $[5] = setRecordingId;
    $[6] = setRecordingNoteState;
    $[7] = tailDrained;
    $[8] = transcriptReady;
    $[9] = t0;
    $[10] = t1;
  } else {
    t0 = $[9];
    t1 = $[10];
  }
  useEffect(t0, t1);
  let t2;
  if ($[11] !== botStatus || $[12] !== isInProgress || $[13] !== isTelehealthNote || $[14] !== tailDrained || $[15] !== transcriptReady) {
    t2 = {
      isTelehealthNote,
      isInProgress,
      botStatus,
      tailDrained,
      transcriptReady
    };
    $[11] = botStatus;
    $[12] = isInProgress;
    $[13] = isTelehealthNote;
    $[14] = tailDrained;
    $[15] = transcriptReady;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function _temp4(s_2) {
  return s_2.setIsTranscribing;
}
function _temp3(s_1) {
  return s_1.setRecordingNoteState;
}
function _temp2(s_0) {
  return s_0.setAudioSource;
}
function _temp(s) {
  return s.setRecordingId;
}